import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Partner } from '../types';
import PartnerForm from './PartnerForm';
import PartnerAkte from './PartnerAkte';
import ViewSelector from './ViewSelector';
import { useCustomFields } from '../hooks/useCustomFields';
import './PartnerList.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface PartnerListProps {
  initialPartnerId?: number | null;
  onInitialPartnerConsumed?: () => void;
}

const PartnerList: React.FC<PartnerListProps> = ({ initialPartnerId, onInitialPartnerConsumed }) => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
  const [filterTyp, setFilterTyp] = useState<string>('');
  const [zeigeInaktive, setZeigeInaktive] = useState<boolean>(false);
  // Geöffnete Kundenakte; bleibt beim Bearbeiten aus der Akte gesetzt, damit
  // Speichern/Abbrechen dorthin zurückführt statt in die Liste.
  const [akteId, setAkteId] = useState<number | null>(null);
  const authToken = localStorage.getItem('auth_token') || '';
  const { definitions: customFields } = useCustomFields('partner', authToken);

  const loadPartners = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterTyp) params.append('typ', filterTyp);
      params.append('nur_aktive', zeigeInaktive ? 'false' : 'true');
      const response = await axios.get(`${API_URL}/api/partners?${params}`);
      setPartners(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Partner:', error);
    } finally {
      setLoading(false);
    }
  }, [filterTyp, zeigeInaktive]);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  useEffect(() => {
    if (initialPartnerId != null) {
      setAkteId(initialPartnerId);
      onInitialPartnerConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPartnerId]);

  const handleEdit = (partner: Partner) => {
    setEditingPartner(partner);
    setShowForm(true);
  };

  const handleSuccess = () => {
    setShowForm(false);
    setEditingPartner(null);
    // Aus der Akte bearbeitet: zurück in die Akte (sie lädt beim Mount neu).
    if (akteId === null) loadPartners();
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingPartner(null);
  };

  const handleDeactivate = async (partner: Partner) => {
    if (!window.confirm(`Partner "${partner.name}" deaktivieren? Er ist danach in neuen Belegen nicht mehr auswählbar, bestehende Daten bleiben erhalten.`)) return;
    try {
      await axios.put(`${API_URL}/api/partners/${partner.partner_id}/deaktivieren`);
      loadPartners();
    } catch (error) {
      console.error('Fehler beim Deaktivieren:', error);
    }
  };

  const handleActivate = async (partner: Partner) => {
    try {
      await axios.put(`${API_URL}/api/partners/${partner.partner_id}/aktivieren`);
      loadPartners();
    } catch (error) {
      console.error('Fehler beim Aktivieren:', error);
    }
  };

  if (showForm) {
    return (
      <PartnerForm
        editingPartner={editingPartner}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
      />
    );
  }

  if (akteId !== null) {
    return (
      <PartnerAkte
        key={akteId}
        partnerId={akteId}
        onBack={() => {
          setAkteId(null);
          loadPartners();
        }}
        onBearbeiten={handleEdit}
      />
    );
  }

  return (
    <div className="partner-list-container">
      <div className="list-header">
        <h2>Kunden & Lieferanten</h2>
        <div className="header-actions">
          <ViewSelector
            bereich="kunden"
            authToken={authToken}
            currentFilter={{ typ: filterTyp, zeigeInaktive }}
            onFilterChange={(filter) => {
              setFilterTyp((filter.typ as string) || '');
              setZeigeInaktive(Boolean(filter.zeigeInaktive));
            }}
          />
          <select
            value={filterTyp}
            onChange={(e) => setFilterTyp(e.target.value)}
            className="filter-select"
          >
            <option value="">Alle</option>
            <option value="Kunde">Kunden</option>
            <option value="Lieferant">Lieferanten</option>
            <option value="Beides">Beides</option>
          </select>
          <label className="filter-checkbox-label">
            <input
              type="checkbox"
              checked={zeigeInaktive}
              onChange={(e) => setZeigeInaktive(e.target.checked)}
            />
            Inaktive anzeigen
          </label>
          <button onClick={() => setShowForm(true)} className="btn-primary">
            + Neuer Partner
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Lädt...</div>
      ) : partners.length === 0 ? (
        <div className="empty-state">
          <p>Keine Partner vorhanden</p>
          <button onClick={() => setShowForm(true)} className="btn-primary">
            Ersten Partner erstellen
          </button>
        </div>
      ) : (
        <div className="table-container">
          <table className="partner-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Typ</th>
                <th>Email</th>
                <th>Telefon</th>
                <th>Zahlungsbedingungen</th>
                {customFields.map(cf => (
                  <th key={cf.feld_id} title={cf.beschreibung || ''}>{cf.label}</th>
                ))}
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {partners.map((partner) => (
                <tr key={partner.partner_id} className={partner.aktiv === false ? 'row-inaktiv' : ''}>
                  <td className="partner-name">
                    <button type="button" className="k-link" title="Kundenakte öffnen"
                            onClick={() => setAkteId(partner.partner_id)}>
                      {partner.name}
                    </button>
                  </td>
                  <td>
                    <span className={`badge badge-${partner.typ.toLowerCase()}`}>
                      {partner.typ}
                    </span>
                  </td>
                  <td>{partner.kontakt_email || '-'}</td>
                  <td>{partner.kontakt_telefon || '-'}</td>
                  <td>{partner.zahlungsbedingungen || '-'}</td>
                  {customFields.map(cf => {
                    const val = ((partner as any).custom_attributes || {})[cf.schluessel];
                    let display: string = '-';
                    if (val !== null && val !== undefined && val !== '') {
                      display = cf.typ === 'BOOLEAN' ? (val ? '✓' : '–') : String(val);
                    }
                    return <td key={cf.feld_id}>{display}</td>;
                  })}
                  <td>
                    <button onClick={() => setAkteId(partner.partner_id)} className="btn-edit">
                      Akte
                    </button>
                    <button
                      onClick={() => handleEdit(partner)}
                      className="btn-edit"
                    >
                      Bearbeiten
                    </button>
                    {partner.aktiv === false ? (
                      <button onClick={() => handleActivate(partner)} className="btn-edit">
                        Aktivieren
                      </button>
                    ) : (
                      <button onClick={() => handleDeactivate(partner)} className="btn-delete">
                        Deaktivieren
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PartnerList;
